import { ReactNode, memo } from 'react';

import styled from 'styled-components';

import { IconHire } from '@components/icons/hire';
import { IconTrigger } from '@components/icons/trigger';
import { Spinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useGetTravellerJourneySource } from 'api/traveller-journeys/hooks';
import {
  TravellerJourneySource,
  TravellerJourneySourceJourney,
} from 'api/traveller-journeys/types';
import { TravellerSource } from 'api/types';
import { ACTION_TYPES } from 'ui/actions/action-config';
import { NodeContainer } from 'ui/traveller-journey-builder/node-types/node-container';
import { TravellerJourneyNode } from 'ui/traveller-journey-builder/node-types/types';

import {
  AddedFromJourneyTriggerIcon,
  ApiTriggerIcon,
  EmailTriggerIcon,
  JourneyFormTriggerIcon,
  ManualTriggerIcon,
  ScheduledPollingTriggerIcon,
  TriggerCard,
  TriggerIcon,
  WebhookTriggerIcon,
} from '../triggers/common';
import { useAddedFromJourneySidebar } from '../triggers/sidebars/added-from-journey-sidebar';
import { useApiSidebar } from '../triggers/sidebars/api-sidebar';
import { useExternalFormSidebar } from '../triggers/sidebars/external-form-sidebar';

const ACTION_TYPE = ACTION_TYPES.journey_triggers;

export const Node = memo((node: TravellerJourneyNode<typeof ACTION_TYPE>) => {
  return (
    <NodeContainer node={node} hideTargetHandle hideSourceHandle disableClick>
      <TravellerJourneyTriggers shortId={node.data.travellerJourneyId} />
      <TriggerIcon />
    </NodeContainer>
  );
});

type TriggerConfig = {
  Icon: ReactNode;
  label: string;
  IconRight?: ReactNode;
  onClick?: () => void;
};

type TravellerJourneyTriggersProps = {
  shortId: string;
};

export const TravellerJourneyTriggers = ({ shortId }: TravellerJourneyTriggersProps) => {
  const { t } = useScopedTranslate(locale);
  const travellerSourceQuery = useGetTravellerJourneySource(shortId);
  const showExternalFormSidebar = useExternalFormSidebar();
  const showApiSidebar = useApiSidebar();
  const showAddedFromJourneySidebar = useAddedFromJourneySidebar();
  const data = travellerSourceQuery.data;

  if (!data || travellerSourceQuery.isLoading) {
    return <Spinner />;
  }

  const sourceInfo = data.sourceInfo;
  const triggerConfigs: Record<TravellerSource, TriggerConfig> = {
    manual: {
      Icon: <ManualTriggerIcon />,
      label: t('manuallyAdded'),
    },
    external_form: {
      Icon: <JourneyFormTriggerIcon />,
      label: t('externalForm'),
      onClick: () => showExternalFormSidebar({ travellerJourneyId: shortId }),
    },
    webhook: { Icon: <WebhookTriggerIcon />, label: t('webhook') },
    email: { Icon: <EmailTriggerIcon />, label: t('emailTrigger') },
    scheduled_polling: { Icon: <ScheduledPollingTriggerIcon />, label: t('scheduledPolling') },
    added_from_journey: {
      Icon: <AddedFromJourneyTriggerIcon />,
      label: t('addedFromAnotherJourney'),
      onClick: () => showAddedFromJourneySidebar({ travellerJourneyId: shortId }),
    },
    hire: { Icon: <IconHire />, label: t('hire') },
    Hire: { Icon: <IconHire />, label: t('hire') },
    portal: {
      Icon: <JourneyFormTriggerIcon />,
      label: t('externalForm'),
      onClick: () => showExternalFormSidebar({ travellerJourneyId: shortId }),
    },
    api: {
      Icon: <ApiTriggerIcon />,
      label: t('api'),
      onClick: () => showApiSidebar({ travellerJourneyId: shortId }),
    },
  };

  const trigger = triggerConfigs[sourceInfo.source];

  const description = <TriggerDescription sourceInfo={sourceInfo} />;

  return (
    <Container>
      <Stack orientation="horizontal" space={2}>
        <IconTrigger color="elementBlue100" />
        <Text font="bold16" text={t('trigger')} />
      </Stack>
      <TriggerCard
        data-testid={`traveller-trigger-${sourceInfo.source}`}
        icon={trigger.Icon}
        title={trigger.label}
        description={description}
        iconRight={trigger.IconRight}
        onClick={trigger.onClick}
      />
    </Container>
  );
};

const getSourceJourneyLabel = (
  sourceJourney: TravellerJourneySourceJourney | null,
  t: ReturnType<typeof useScopedTranslate<typeof locale>>['t']
) => {
  if (sourceJourney?.access === 'accessible') return sourceJourney.title;
  if (sourceJourney?.access === 'private') return t('privateJourney');
  return t('journeyUnavailable');
};

const TriggerDescription = ({
  sourceInfo,
}: {
  sourceInfo: TravellerJourneySource['sourceInfo'];
}) => {
  const { t } = useScopedTranslate(locale);

  switch (sourceInfo.source) {
    case 'manual':
      return (
        <Stack space={0}>
          <Text
            font="regular12"
            color="textSecondary"
            tooltip={sourceInfo.email}
            text={t('addedBy', {
              name: sourceInfo.name ?? '',
            })}
          />
        </Stack>
      );
    case 'external_form':
    case 'portal':
      return (
        <Stack space={0}>
          <Text font="regular12" color="textSecondary" text={sourceInfo.name} />
          {sourceInfo.submitterName ? (
            <Text
              font="regular12"
              color="textSecondary"
              tooltip={sourceInfo.submitterEmail ?? ''}
              text={t('submittedBy', { name: sourceInfo.submitterName })}
            />
          ) : null}
        </Stack>
      );
    case 'api':
      return sourceInfo.name ? (
        <Stack space={0}>
          <Text
            font="regular12"
            color="textSecondary"
            text={t('createdBy', { name: sourceInfo.name })}
          />
        </Stack>
      ) : null;
    case 'email': {
      const senderLabel = sourceInfo.submitterName || sourceInfo.submitterEmail;
      if (!sourceInfo.name && !senderLabel) return null;
      return (
        <Stack space={0}>
          {sourceInfo.name ? (
            <Text font="regular12" color="textSecondary" text={sourceInfo.name} />
          ) : null}
          {senderLabel ? (
            <Text
              font="regular12"
              color="textSecondary"
              tooltip={sourceInfo.submitterEmail ?? ''}
              text={t('receivedFrom', { name: senderLabel })}
            />
          ) : null}
        </Stack>
      );
    }
    case 'added_from_journey': {
      const label = getSourceJourneyLabel(sourceInfo.sourceJourney, t);
      return (
        <Stack space={0}>
          <Text font="regular12" color="textSecondary" text={label} />
        </Stack>
      );
    }
    default:
      return null;
  }
};

const Container = styled(Stack).attrs({ space: 4 })({
  width: pxToRem(352),
  border: `${pxToRem(1)} solid ${theme.color.border10}`,
  backgroundColor: theme.color.backgroundLightDark,
  padding: pxToRem(16),
  borderRadius: pxToRem(8),
});

const locale = {
  en: {
    title: 'Start event',
    automaticDescription: 'Started automatically for traveller entering journey',
    manualDescription: 'Started manually for traveller entering journey',

    trigger: 'Trigger',
    manuallyAdded: 'Manually added',
    webhook: 'Webhook',
    emailTrigger: 'Mailhook',
    externalForm: 'External form',
    scheduledPolling: 'Scheduled polling',
    addedFromAnotherJourney: 'Added from another journey',
    privateJourney: 'Private',
    journeyUnavailable: 'Journey no longer available',
    hire: '50.Hire',
    api: 'API',
    addedBy: 'Added by: {name}',
    createdBy: 'Created by: {name}',
    submittedBy: 'Submitted by: {name}',
    receivedFrom: 'Received from: {name}',
  },
};
